import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Check,
  Gift,
  Inbox,
  LoaderCircle,
  MessageCircle,
  RefreshCw,
  Sparkles,
  X,
} from 'lucide-react';
import {
  claimSystemMessage,
  getSystemMessages,
  readAllSystemMessages,
  readSystemMessage,
  systemMessageApiError,
} from './systemMessageApi';
import './SystemMessageDialog.css';
import { replaceBrandValue, useTeamBrand } from '../branding/TeamBrandContext';

const CATEGORIES = [
  { value: 'service', label: '服务', labelEn: 'Service' },
  { value: 'activity', label: '活动', labelEn: 'Activity' },
  { value: 'reward', label: '奖励', labelEn: 'Rewards' },
];

const EMPTY_LABELS = {
  service: ['暂无服务通知', 'No service messages'],
  activity: ['暂无活动消息', 'No activity messages'],
  reward: ['暂无待领取奖励', 'No rewards available'],
};

const PREVIEW_MESSAGES = {
  service: [{
    id: 'preview-service', category: 'service', title: '欢迎使用小影', summary: '产品更新与服务提醒会展示在这里。',
    content: '这是消息中心的预览内容。正式环境会读取管理后台已经发布的服务消息。', published_at: new Date().toISOString(), unread: true,
  }],
  activity: [],
  reward: [{
    id: 'preview-reward', category: 'reward', title: '新用户创作奖励', summary: '完成领取后积分将直接入账。',
    content: '奖励由小影管理后台发布，领取记录会同步进入点数明细。', reward_points: 100, published_at: new Date().toISOString(), unread: true, claimable: true,
  }],
};

function formatDate(value, english) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(english ? 'en-US' : 'zh-CN', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date);
}

function MessageEmpty({ category, english, unreadOnly = false }) {
  const label = unreadOnly
    ? (english ? 'No unread messages' : '暂无未读消息')
    : EMPTY_LABELS[category][english ? 1 : 0];
  return (
    <div className="system-message-empty">
      <span><Inbox size={21} /></span>
      <p>{label}</p>
    </div>
  );
}

function MessageList({ messages, selectedId, category, english, unreadOnly, onSelect }) {
  if (!messages.length) return <MessageEmpty category={category} english={english} unreadOnly={unreadOnly} />;
  return (
    <div className="system-message-list">
      {messages.map(message => (
        <button
          type="button"
          key={message.id}
          className={`system-message-list__item${String(selectedId) === String(message.id) ? ' is-active' : ''}`}
          onClick={() => onSelect(message)}
        >
          <span className="system-message-list__heading">
            <strong>{message.title}</strong>
            {message.unread && <i aria-label={english ? 'Unread' : '未读'} />}
          </span>
          <span className="system-message-list__summary">{message.summary || message.content}</span>
          <time>{formatDate(message.published_at, english)}</time>
        </button>
      ))}
    </div>
  );
}

function MessageDetail({ message, english, onBack }) {
  if (!message) {
    return (
      <div className="system-message-detail__placeholder">
        <span><MessageCircle size={23} /></span>
        <p>{english ? 'Select a message to read' : '请选择一条消息'}</p>
      </div>
    );
  }
  return (
    <article className="system-message-detail__article">
      <button type="button" className="system-message-detail__back" onClick={onBack}>
        <ArrowLeft size={14} />{english ? 'Back to messages' : '返回消息列表'}
      </button>
      <header>
        <span>{CATEGORIES.find(item => item.value === message.category)?.[english ? 'labelEn' : 'label']}</span>
        <h2>{message.title}</h2>
        <time>{formatDate(message.published_at, english)}</time>
      </header>
      {message.summary && <p className="system-message-detail__summary">{message.summary}</p>}
      <div className="system-message-detail__content">{message.content}</div>
    </article>
  );
}

function RewardPanel({ messages, user, english, busyAction, claimableCount, onClaim, onClaimAll }) {
  const claimableMessages = messages.filter(message => message.claimable);
  const teamName = user?.team_name || user?.current_team_name;
  const accountName = teamName || (user?.username
    ? (english ? `${user.username}'s team` : `${user.username}的团队`)
    : (english ? 'Current account' : '当前账户'));
  return (
    <section className="system-message-rewards" aria-label={english ? 'Reward messages' : '奖励消息'}>
      <div className="system-message-rewards__account">
        <span>{english ? 'Credit to' : '入账至'}</span>
        <strong>{accountName}</strong>
      </div>
      <div className="system-message-rewards__list">
        {!messages.length ? <MessageEmpty category="reward" english={english} /> : messages.map(message => (
          <article className="system-message-reward-card" key={message.id}>
            <span className="system-message-reward-card__icon"><Sparkles size={17} /></span>
            <div className="system-message-reward-card__copy">
              <div><h2>{message.title}</h2><time>{formatDate(message.published_at, english)}</time></div>
              <p>{message.summary || message.content}</p>
              {message.expires_at && <small>{english ? 'Expires ' : '有效期至 '}{formatDate(message.expires_at, english)}</small>}
            </div>
            <div className="system-message-reward-card__action">
              <strong>+{Number(message.reward_points || 0).toLocaleString(english ? 'en-US' : 'zh-CN')}</strong>
              <span>{english ? 'credits' : '点数'}</span>
              <button
                type="button"
                disabled={!message.claimable || Boolean(busyAction)}
                onClick={() => onClaim(message)}
              >
                {busyAction === `claim-${message.id}` ? <LoaderCircle className="is-spinning" size={13} /> : message.claimable ? <Gift size={13} /> : <Check size={13} />}
                {message.claimable ? (english ? 'Claim' : '领取') : (english ? 'Claimed' : '已领取')}
              </button>
            </div>
          </article>
        ))}
      </div>
      <footer className="system-message-rewards__footer">
        <span>{claimableCount} {english ? 'rewards available' : '个待领取'}</span>
        <button type="button" disabled={!claimableMessages.length || Boolean(busyAction)} onClick={onClaimAll}>
          {busyAction === 'claim-all' && <LoaderCircle className="is-spinning" size={13} />}
          {english ? 'Claim all' : '全部领取'}
        </button>
      </footer>
    </section>
  );
}

export default function SystemMessageDialog({
  open,
  onClose,
  user,
  english = false,
  preview = false,
  onCreditsChanged,
  onUnreadChange,
}) {
  const brand = useTeamBrand();
  const [category, setCategory] = useState('service');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({ service: 0, activity: 0, reward: 0 });
  const [claimableCount, setClaimableCount] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [busyAction, setBusyAction] = useState('');
  const closeButtonRef = useRef(null);
  const requestSequence = useRef(0);

  const updateCounts = useCallback(counts => {
    const next = {
      service: Number(counts?.service || 0),
      activity: Number(counts?.activity || 0),
      reward: Number(counts?.reward || 0),
    };
    setUnreadCounts(next);
    onUnreadChange?.(next.service + next.activity + next.reward);
  }, [onUnreadChange]);

  const loadMessages = useCallback(async (nextCategory = category, nextUnreadOnly = unreadOnly) => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError('');
    setFeedback(null);
    try {
      if (preview) {
        const all = PREVIEW_MESSAGES[nextCategory] || [];
        const list = nextUnreadOnly ? all.filter(message => message.unread) : all;
        const counts = Object.fromEntries(Object.entries(PREVIEW_MESSAGES).map(([key, value]) => [key, value.filter(item => item.unread).length]));
        if (requestId !== requestSequence.current) return;
        setMessages(list);
        updateCounts(counts);
        setClaimableCount(PREVIEW_MESSAGES.reward.filter(message => message.claimable).length);
        return;
      }
      const data = await getSystemMessages(nextCategory, { unreadOnly: nextUnreadOnly, perPage: 100 });
      if (requestId !== requestSequence.current) return;
      setMessages(data?.list || []);
      updateCounts(data?.unread_counts);
      setClaimableCount(Number(data?.claimable_count || 0));
    } catch (requestError) {
      if (requestId !== requestSequence.current) return;
      setMessages([]);
      setError(getErrorMessage(systemMessageApiError(requestError, '消息加载失败，请稍后重试')));
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [category, preview, unreadOnly, updateCounts]);

  useEffect(() => {
    if (!open) return undefined;
    setSelectedId(null);
    loadMessages();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = event => { if (event.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handleKeyDown);
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());
    return () => {
      requestSequence.current += 1;
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [loadMessages, onClose, open]);

  const visibleMessages = useMemo(
    () => replaceBrandValue(messages, brand.active ? brand.name : ''),
    [brand.active, brand.name, messages],
  );
  const selectedMessage = useMemo(
    () => visibleMessages.find(message => String(message.id) === String(selectedId)) || null,
    [visibleMessages, selectedId],
  );
  const currentUnread = unreadCounts[category] || 0;

  const changeCategory = nextCategory => {
    if (nextCategory === category) return;
    setCategory(nextCategory);
    setUnreadOnly(false);
    setSelectedId(null);
  };

  const selectMessage = async message => {
    setSelectedId(message.id);
    if (!message.unread || preview) return;
    setMessages(items => items.map(item => String(item.id) === String(message.id) ? { ...item, unread: false } : item));
    updateCounts({ ...unreadCounts, [category]: Math.max(0, currentUnread - 1) });
    try {
      await readSystemMessage(message.id);
    } catch (readError) {
      setMessages(items => items.map(item => String(item.id) === String(message.id) ? { ...item, unread: true } : item));
      updateCounts({ ...unreadCounts, [category]: currentUnread });
      setFeedback({ type: 'error', text: getErrorMessage(systemMessageApiError(readError, '标记已读失败')) });
    }
  };

  const markAllRead = async () => {
    if (!currentUnread || busyAction) return;
    setBusyAction('read-all');
    setFeedback(null);
    try {
      if (!preview) await readAllSystemMessages(category);
      updateCounts({ ...unreadCounts, [category]: 0 });
      setMessages(items => unreadOnly ? [] : items.map(item => ({ ...item, unread: false })));
      if (unreadOnly) setSelectedId(null);
      setFeedback({ type: 'success', text: english ? 'All messages in this category are marked as read.' : '本分类消息已全部标记为已读' });
    } catch (readError) {
      setFeedback({ type: 'error', text: getErrorMessage(systemMessageApiError(readError, '全部已读操作失败')) });
    } finally {
      setBusyAction('');
    }
  };

  const claimOne = async message => {
    if (!message.claimable || busyAction) return;
    setBusyAction(`claim-${message.id}`);
    setFeedback(null);
    try {
      if (!preview) await claimSystemMessage(message.id);
      await loadMessages('reward', false);
      await onCreditsChanged?.();
      setFeedback({ type: 'success', text: english ? `${message.reward_points} credits claimed.` : `${message.reward_points} 点数已入账` });
    } catch (claimError) {
      setFeedback({ type: 'error', text: getErrorMessage(systemMessageApiError(claimError, '奖励领取失败')) });
    } finally {
      setBusyAction('');
    }
  };

  const claimAll = async () => {
    const claimable = messages.filter(message => message.claimable);
    if (!claimable.length || busyAction) return;
    setBusyAction('claim-all');
    setFeedback(null);
    let claimed = 0;
    try {
      if (preview) {
        claimed = claimable.length;
      } else {
        for (const message of claimable) {
          await claimSystemMessage(message.id);
          claimed += 1;
        }
      }
      await loadMessages('reward', false);
      await onCreditsChanged?.();
      setFeedback({ type: 'success', text: english ? `${claimed} rewards claimed.` : `已领取 ${claimed} 个奖励，点数已入账` });
    } catch (claimError) {
      await loadMessages('reward', false);
      if (claimed) await onCreditsChanged?.();
      setFeedback({
        type: 'error',
        text: getErrorMessage(claimed
          ? (`已领取 ${claimed} 个，其余奖励领取失败，请稍后重试`)
          : systemMessageApiError(claimError, '奖励领取失败')),
      });
    } finally {
      setBusyAction('');
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="system-message-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onClose?.(); }}>
      <section id="system-message-dialog" className="system-message-dialog" role="dialog" aria-modal="true" aria-labelledby="system-message-title">
        <header className="system-message-dialog__header">
          <div>
            <h1 id="system-message-title">{english ? 'My messages' : '我的消息'}</h1>
            <p>{english ? 'Service updates, events and rewards in one place.' : '集中查看服务通知、活动动态与点数奖励'}</p>
          </div>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label={english ? 'Close messages' : '关闭我的消息'}><X size={18} /></button>
        </header>

        <div className="system-message-tabs" role="tablist" aria-label={english ? 'Message categories' : '消息分类'}>
          {CATEGORIES.map(item => {
            const count = unreadCounts[item.value] || 0;
            return (
              <button
                type="button"
                role="tab"
                key={item.value}
                aria-selected={category === item.value}
                className={category === item.value ? 'is-active' : ''}
                onClick={() => changeCategory(item.value)}
              >
                {english ? item.labelEn : item.label}
                {count > 0 && <span>{count > 99 ? '99+' : count}</span>}
              </button>
            );
          })}
        </div>

        {category !== 'reward' && (
          <div className="system-message-toolbar">
            <label>
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={event => { setUnreadOnly(event.target.checked); setSelectedId(null); }}
              />
              <span>{english ? `Unread only (${currentUnread})` : `仅看未读 (${currentUnread})`}</span>
            </label>
            <button type="button" onClick={markAllRead} disabled={!currentUnread || Boolean(busyAction)}>
              {busyAction === 'read-all' && <LoaderCircle className="is-spinning" size={12} />}
              {english ? 'Mark all read' : '全部已读'}
            </button>
          </div>
        )}

        {feedback && (
          <div className={`system-message-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>
            {feedback.type === 'success' && <Check size={13} />}{feedback.text}
          </div>
        )}

        <div className={`system-message-dialog__body${selectedMessage ? ' has-selection' : ''}`}>
          {loading ? (
            <div className="system-message-loading" role="status"><LoaderCircle className="is-spinning" size={20} /><span>{english ? 'Loading messages…' : '正在加载消息…'}</span></div>
          ) : error ? (
            <div className="system-message-error" role="alert"><p>{error}</p><button type="button" onClick={() => loadMessages()}><RefreshCw size={13} />{english ? 'Try again' : '重新加载'}</button></div>
          ) : category === 'reward' ? (
            <RewardPanel
              messages={visibleMessages}
              user={user}
              english={english}
              busyAction={busyAction}
              claimableCount={claimableCount}
              onClaim={claimOne}
              onClaimAll={claimAll}
            />
          ) : (
            <div className="system-message-workspace">
              <aside className="system-message-list-pane">
                <MessageList messages={visibleMessages} selectedId={selectedId} category={category} english={english} unreadOnly={unreadOnly} onSelect={selectMessage} />
              </aside>
              <section className="system-message-detail">
                <MessageDetail message={selectedMessage} english={english} onBack={() => setSelectedId(null)} />
              </section>
            </div>
          )}
        </div>
      </section>
    </div>,
    document.body,
  );
}
